import type {
  GeneratedWorkout,
  WorkoutBlock,
  IntervalNode,
  RepeatNode,
  TrainingPhase,
} from "@/types";

/**
 * Mock workout data for testing the calendar view
 */

// Helper to get the start of current week (Monday)
export function getWeekStart(date: Date = new Date()): string {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1); // adjust when day is Sunday
  d.setDate(diff);
  // Use LOCAL date components — toISOString() is UTC and drifts in non-UTC timezones
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

// Sample workout blocks
const warmupBlock: WorkoutBlock = {
  id: "warmup-1",
  name: "Warm Up",
  kind: "warmup",
  children: [
    {
      type: "interval",
      id: "warmup-interval-1",
      name: "Easy Spin",
      durationSec: 600, // 10 min
      target: {
        power: { kind: "percent_ftp", low: 50, high: 65 },
        rpe: { low: 3, high: 4 },
      },
      intensity: "warmup",
    },
  ],
};

const sweetSpotBlock: WorkoutBlock = {
  id: "work-1",
  name: "Sweet Spot",
  kind: "work",
  children: [
    {
      type: "repeat",
      id: "repeat-1",
      times: 3,
      children: [
        {
          type: "interval",
          id: "ss-interval-1",
          name: "Sweet Spot",
          durationSec: 480, // 8 min
          target: {
            power: { kind: "percent_ftp", low: 88, high: 93 },
            rpe: { low: 7, high: 8 },
          },
          cadenceCue: "85-95 rpm",
          intensity: "active",
        },
        {
          type: "interval",
          id: "recovery-1",
          name: "Recovery",
          durationSec: 180, // 3 min
          target: {
            power: { kind: "percent_ftp", low: 55, high: 65 },
            rpe: { low: 4, high: 5 },
          },
          intensity: "rest",
        },
      ],
    },
  ],
};

const cooldownBlock: WorkoutBlock = {
  id: "cooldown-1",
  name: "Cool Down",
  kind: "cooldown",
  children: [
    {
      type: "interval",
      id: "cooldown-interval-1",
      name: "Easy Spin",
      durationSec: 600, // 10 min
      target: {
        power: { kind: "percent_ftp", low: 45, high: 55 },
        rpe: { low: 2, high: 3 },
      },
      intensity: "cooldown",
    },
  ],
};

const thresholdBlock: WorkoutBlock = {
  id: "work-2",
  name: "Threshold Intervals",
  kind: "work",
  children: [
    {
      type: "repeat",
      id: "repeat-2",
      times: 4,
      children: [
        {
          type: "interval",
          id: "threshold-1",
          name: "FTP Threshold",
          durationSec: 300, // 5 min
          target: {
            power: { kind: "percent_ftp", low: 95, high: 105 },
            rpe: { low: 8, high: 9 },
          },
          cadenceCue: "90-100 rpm",
          intensity: "active",
        },
        {
          type: "interval",
          id: "recovery-2",
          name: "Recovery",
          durationSec: 240, // 4 min
          target: {
            power: { kind: "percent_ftp", low: 50, high: 60 },
            rpe: { low: 3, high: 4 },
          },
          intensity: "rest",
        },
      ],
    },
  ],
};

const enduranceBlock: WorkoutBlock = {
  id: "work-3",
  name: "Endurance Ride",
  kind: "work",
  children: [
    {
      type: "interval",
      id: "endurance-1",
      name: "Zone 2 Endurance",
      durationSec: 2700, // 45 min
      target: {
        power: { kind: "percent_ftp", low: 56, high: 75 },
        rpe: { low: 4, high: 6 },
      },
      cadenceCue: "80-90 rpm",
      intensity: "active",
    },
  ],
};

// VO2max intervals for peak phase
const vo2maxBlock: WorkoutBlock = {
  id: "work-4",
  name: "VO2max Intervals",
  kind: "work",
  children: [
    {
      type: "repeat",
      id: "repeat-vo2",
      times: 5,
      children: [
        {
          type: "interval",
          id: "vo2max-1",
          name: "VO2max",
          durationSec: 180, // 3 min
          target: {
            power: { kind: "percent_ftp", low: 106, high: 120 },
            rpe: { low: 9, high: 10 },
          },
          cadenceCue: "95-105 rpm",
          intensity: "active",
        },
        {
          type: "interval",
          id: "recovery-vo2",
          name: "Recovery",
          durationSec: 180, // 3 min
          target: {
            power: { kind: "percent_ftp", low: 50, high: 60 },
            rpe: { low: 3, high: 4 },
          },
          intensity: "rest",
        },
      ],
    },
  ],
};

// Long endurance for base phase
const longEnduranceBlock: WorkoutBlock = {
  id: "work-5",
  name: "Long Endurance",
  kind: "work",
  children: [
    {
      type: "interval",
      id: "long-endurance-1",
      name: "Zone 2 Long Ride",
      durationSec: 4500, // 75 min
      target: {
        power: { kind: "percent_ftp", low: 56, high: 75 },
        rpe: { low: 4, high: 6 },
      },
      cadenceCue: "80-90 rpm",
      intensity: "active",
    },
  ],
};

// Recovery ride
const recoveryBlock: WorkoutBlock = {
  id: "work-6",
  name: "Recovery Ride",
  kind: "work",
  children: [
    {
      type: "interval",
      id: "recovery-easy",
      name: "Easy Recovery",
      durationSec: 1800, // 30 min
      target: {
        power: { kind: "percent_ftp", low: 45, high: 55 },
        rpe: { low: 2, high: 3 },
      },
      cadenceCue: "80-90 rpm",
      intensity: "rest",
    },
  ],
};

// Tempo intervals for build phase
const tempoBlock: WorkoutBlock = {
  id: "work-7",
  name: "Tempo Intervals",
  kind: "work",
  children: [
    {
      type: "repeat",
      id: "repeat-tempo",
      times: 3,
      children: [
        {
          type: "interval",
          id: "tempo-1",
          name: "Tempo",
          durationSec: 720, // 12 min
          target: {
            power: { kind: "percent_ftp", low: 76, high: 87 },
            rpe: { low: 6, high: 7 },
          },
          cadenceCue: "85-95 rpm",
          intensity: "active",
        },
        {
          type: "interval",
          id: "recovery-tempo",
          name: "Recovery",
          durationSec: 300, // 5 min
          target: {
            power: { kind: "percent_ftp", low: 50, high: 60 },
            rpe: { low: 3, high: 4 },
          },
          intensity: "rest",
        },
      ],
    },
  ],
};

// Generate mock workouts for the current week
export function generateMockWeeklyWorkouts(
  accountId: string,
  phase?: TrainingPhase,
): GeneratedWorkout[] {
  const weekStart = getWeekStart();
  const currentPhase = phase || "maintenance";

  // Define workouts based on training phase
  switch (currentPhase) {
    case "base":
      // Base phase: Focus on volume and aerobic endurance
      return [
        {
          id: "workout-mon",
          accountId,
          weekStartDate: weekStart,
          workoutName: "Endurance Ride",
          sourceTemplateId: "template-endurance",
          blocksResolved: [warmupBlock, enduranceBlock, cooldownBlock],
          targetsMode: "power",
          createdAt: new Date().toISOString(),
        },
        {
          id: "workout-wed",
          accountId,
          weekStartDate: weekStart,
          workoutName: "Sweet Spot",
          sourceTemplateId: "template-ss",
          blocksResolved: [warmupBlock, sweetSpotBlock, cooldownBlock],
          targetsMode: "power",
          createdAt: new Date().toISOString(),
        },
        {
          id: "workout-sat",
          accountId,
          weekStartDate: weekStart,
          workoutName: "Long Endurance Ride",
          sourceTemplateId: "template-long",
          blocksResolved: [warmupBlock, longEnduranceBlock, cooldownBlock],
          targetsMode: "power",
          createdAt: new Date().toISOString(),
        },
      ];

    case "build":
      // Build phase: Increase intensity, more threshold work
      return [
        {
          id: "workout-tue",
          accountId,
          weekStartDate: weekStart,
          workoutName: "Sweet Spot Intervals",
          sourceTemplateId: "template-ss",
          blocksResolved: [warmupBlock, sweetSpotBlock, cooldownBlock],
          targetsMode: "power",
          createdAt: new Date().toISOString(),
        },
        {
          id: "workout-thu",
          accountId,
          weekStartDate: weekStart,
          workoutName: "FTP Threshold",
          sourceTemplateId: "template-ftp",
          blocksResolved: [warmupBlock, thresholdBlock, cooldownBlock],
          targetsMode: "power",
          createdAt: new Date().toISOString(),
        },
        {
          id: "workout-sat",
          accountId,
          weekStartDate: weekStart,
          workoutName: "Tempo Intervals",
          sourceTemplateId: "template-tempo",
          blocksResolved: [warmupBlock, tempoBlock, cooldownBlock],
          targetsMode: "power",
          createdAt: new Date().toISOString(),
        },
      ];

    case "peak":
      // Peak phase: Race-specific high intensity
      return [
        {
          id: "workout-tue",
          accountId,
          weekStartDate: weekStart,
          workoutName: "VO2max Intervals",
          sourceTemplateId: "template-vo2",
          blocksResolved: [warmupBlock, vo2maxBlock, cooldownBlock],
          targetsMode: "power",
          createdAt: new Date().toISOString(),
        },
        {
          id: "workout-thu",
          accountId,
          weekStartDate: weekStart,
          workoutName: "FTP Threshold",
          sourceTemplateId: "template-ftp",
          blocksResolved: [warmupBlock, thresholdBlock, cooldownBlock],
          targetsMode: "power",
          createdAt: new Date().toISOString(),
        },
        {
          id: "workout-sat",
          accountId,
          weekStartDate: weekStart,
          workoutName: "Sweet Spot",
          sourceTemplateId: "template-ss",
          blocksResolved: [warmupBlock, sweetSpotBlock, cooldownBlock],
          targetsMode: "power",
          createdAt: new Date().toISOString(),
        },
      ];

    case "taper":
      // Taper phase: Reduced volume, maintain intensity
      return [
        {
          id: "workout-tue",
          accountId,
          weekStartDate: weekStart,
          workoutName: "Short Threshold",
          sourceTemplateId: "template-ftp",
          blocksResolved: [warmupBlock, thresholdBlock, cooldownBlock],
          targetsMode: "power",
          createdAt: new Date().toISOString(),
        },
        {
          id: "workout-fri",
          accountId,
          weekStartDate: weekStart,
          workoutName: "Easy Recovery",
          sourceTemplateId: "template-recovery",
          blocksResolved: [warmupBlock, recoveryBlock, cooldownBlock],
          targetsMode: "power",
          createdAt: new Date().toISOString(),
        },
      ];

    case "maintenance":
    default:
      // Maintenance: Balanced mix
      return [
        {
          id: "workout-mon",
          accountId,
          weekStartDate: weekStart,
          workoutName: "Sweet Spot Intervals",
          sourceTemplateId: "template-ss",
          blocksResolved: [warmupBlock, sweetSpotBlock, cooldownBlock],
          targetsMode: "power",
          createdAt: new Date().toISOString(),
        },
        {
          id: "workout-wed",
          accountId,
          weekStartDate: weekStart,
          workoutName: "FTP Threshold",
          sourceTemplateId: "template-ftp",
          blocksResolved: [warmupBlock, thresholdBlock, cooldownBlock],
          targetsMode: "power",
          createdAt: new Date().toISOString(),
        },
        {
          id: "workout-fri",
          accountId,
          weekStartDate: weekStart,
          workoutName: "Recovery Endurance",
          sourceTemplateId: "template-endurance",
          blocksResolved: [warmupBlock, enduranceBlock, cooldownBlock],
          targetsMode: "power",
          createdAt: new Date().toISOString(),
        },
      ];
  }
}

function nodeSeconds(node: IntervalNode | RepeatNode): number {
  if (node.type === "interval") return node.durationSec;
  return (
    node.times *
    node.children.reduce((sum, child) => sum + nodeSeconds(child), 0)
  );
}

// Calculate total workout duration in minutes
export function calculateWorkoutDuration(blocks: WorkoutBlock[]): number {
  const totalSeconds = blocks.reduce(
    (blockSum, block) =>
      blockSum +
      block.children.reduce((nodeSum, node) => nodeSum + nodeSeconds(node), 0),
    0,
  );

  return Math.round(totalSeconds / 60);
}

// Get a specific workout by ID
export function getMockWorkout(
  workoutId?: string,
): GeneratedWorkout | undefined {
  const mockWorkouts = generateMockWeeklyWorkouts("demo-account");
  return mockWorkouts.find((w) => w.id === workoutId);
}
